'use client';

import React from 'react';
import { FileDown } from 'lucide-react';
import axiosInstance, { endpoints } from '@/lib/axios';
import Button from '@/components/ui/Button';
import { INPUT_CLASS, CONTROL_HEIGHT } from './formStyles';
import useModalDismiss from '@/hooks/useModalDismiss';

interface DownloadProgramModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Formation name shown in the subtitle (e.g. "…au programme de {programName}"). */
    programName?: string;
    /** Slug used to fetch the downloadable program PDF once the lead is captured. */
    formationSlug?: string;
    /** Truthy only when the formation actually has an uploaded program PDF. */
    programPdfUrl?: string | null;
    origin?: string;
    message?: string;
}

/**
 * Fetches the formation's program PDF (as an attachment, via the API so CORS
 * applies) and triggers a browser download. Kept outside the component since it
 * relies only on module-level imports.
 */
const downloadProgramme = async (slug: string, programName?: string) => {
    const response = await axiosInstance.get(endpoints.formations.programme(slug), {
        responseType: 'blob',
    });

    const blobUrl = window.URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `Programme-${(programName ?? 'formation').trim().replace(/\s+/g, '-')}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

/**
 * "Télécharger le programme" lead-capture modal.
 *
 * Mirrors {@link ContactModal}: same field set (+33 phone), same leads
 * endpoint and submit flow, same design tokens — specialised header/CTA for
 * requesting a link to a formation's program. Kept presentational + self
 * contained; the parent owns the open/close state.
 */
const DownloadProgramModal: React.FC<DownloadProgramModalProps> = ({
    isOpen,
    onClose,
    programName,
    formationSlug,
    programPdfUrl,
    // Backend accepts only: form | quiz | contact | formation | chat | other
    origin = 'formation',
    message,
}) => {
    const [formData, setFormData] = React.useState({
        prenom: '',
        nom: '',
        email: '',
        phone: '',
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');
    const titleId = React.useId();

    useModalDismiss(isOpen, onClose);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const isFormValid = Object.values(formData).every(value => value.trim() !== '');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid) return;

        setSubmitting(true);
        setError('');

        // 1. Capture the lead — this is the critical step. If it fails, keep the
        //    modal open so the user can retry.
        try {
            await axiosInstance.post(endpoints.leads.create, {
                first_name: formData.prenom,
                last_name: formData.nom,
                email: formData.email,
                phone: `+33${formData.phone}`,
                message:
                    message ??
                    `Demande de programme${programName ? ` — ${programName}` : ''}`,
                origin,
            });
        } catch {
            setError("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
            setSubmitting(false);
            return;
        }

        // 2. Start the program download. Best-effort: the lead is already saved,
        //    so a download failure must not push the user to resubmit (which would
        //    create a duplicate lead). Only attempt it when a PDF actually exists.
        if (formationSlug && programPdfUrl) {
            try {
                await downloadProgramme(formationSlug, programName);
            } catch {
                /* download failed — lead is saved; let the user proceed */
            }
        }
        setFormData({ prenom: '', nom: '', email: '', phone: '' });
        setSubmitting(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[100050] flex items-center justify-center p-4 bg-[#FFFFFF4D] backdrop-blur-xs"
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            {/* max-h + scroll: the form is ~480px tall, which had nowhere to go on
                a short phone. */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative w-full max-w-170 max-h-[90vh] overflow-y-auto bg-white rounded-lg pt-12 pb-6 sm:pb-10 px-5 sm:px-10 border border-[#40BFDE]"
                style={{ boxShadow: "0px 1px 2px 1px #00000014" }}
            >
                {/* Close Button */}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fermer"
                    className="absolute top-4 right-4 inline-flex size-9 items-center justify-center rounded-full text-[#1B365E] hover:text-[#40BFDE] transition-colors cursor-pointer duration-75"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23.2897 4.0987C24.2268 3.16164 24.2268 1.63986 23.2897 0.702796C22.3526 -0.234265 20.8309 -0.234265 19.8938 0.702796L12 8.60409L4.0987 0.710292C3.16164 -0.226769 1.63986 -0.226769 0.702796 0.710292C-0.234265 1.64735 -0.234265 3.16914 0.702796 4.1062L8.60409 12L0.710292 19.9013C-0.226769 20.8384 -0.226769 22.3601 0.710292 23.2972C1.64735 24.2343 3.16914 24.2343 4.1062 23.2972L12 15.3959L19.9013 23.2897C20.8384 24.2268 22.3601 24.2268 23.2972 23.2897C24.2343 22.3526 24.2343 20.8309 23.2972 19.8938L15.3959 12L23.2897 4.0987Z" fill="currentColor" />
                    </svg>
                </button>

                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4">
                        <div className="size-14 sm:size-16 shrink-0 rounded-full bg-[#C7F8FF] flex items-center justify-center">
                            <FileDown className="size-7 text-[#40BFDE]" aria-hidden="true" />
                        </div>
                        <h2 id={titleId} className="font-red-hat font-light text-[24px] sm:text-[32px] leading-8 sm:leading-10 text-[#1B365E] tracking-[0.9px]">
                            Télécharger le programme
                        </h2>
                    </div>
                    <p className="text-center font-bold text-[16px] sm:text-[18px] leading-6 sm:leading-7 text-[#1B365E] tracking-[0.4px] max-w-md mx-auto">
                        Vous recevrez un lien pour accéder au programme de {programName ?? 'cette formation'}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 max-w-103 mx-auto">
                    <input
                        type="text"
                        name="prenom"
                        aria-label="Prénom"
                        placeholder="Prénom"
                        autoComplete="given-name"
                        value={formData.prenom}
                        onChange={handleChange}
                        className={INPUT_CLASS}
                    />
                    <input
                        type="text"
                        name="nom"
                        aria-label="Nom"
                        placeholder="Nom"
                        autoComplete="family-name"
                        value={formData.nom}
                        onChange={handleChange}
                        className={INPUT_CLASS}
                    />
                    <input
                        type="email"
                        name="email"
                        aria-label="Adresse e-mail"
                        placeholder="Email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={INPUT_CLASS}
                    />
                    <div className="flex gap-2.5 sm:gap-4.5">
                        <div
                            aria-hidden="true"
                            className={`w-14 ${CONTROL_HEIGHT} shrink-0 px-3 flex items-center justify-center rounded-lg border border-[#ABABAB] bg-[#EFEFEF] text-[13px] font-normal text-[#1B365E]`}
                        >
                            +33
                        </div>
                        <input
                            type="tel"
                            inputMode="tel"
                            name="phone"
                            aria-label="Numéro de portable"
                            placeholder="Portable"
                            autoComplete="tel-national"
                            value={formData.phone}
                            onChange={handleChange}
                            className={INPUT_CLASS}
                        />
                    </div>

                    <p className="text-[12px] text-[#1B365E] font-medium tracking-[0.25px]">
                        *Tous les champs sont obligatoires
                    </p>

                    {error && <p role="alert" className="text-[12px] text-red-600">{error}</p>}

                    <Button
                        type="submit"
                        variant="primary"
                        size="unset"
                        disabled={!isFormValid || submitting}
                        className="w-full h-12 sm:h-14.5 rounded-lg text-[16px]"
                    >
                        {submitting ? "Envoi..." : "Envoyer ma demande"}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default DownloadProgramModal;
